import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, TextField, Button, Alert, InputAdornment, IconButton, LinearProgress } from '@mui/material';
import axios from 'axios';

const API = '/api';

const getPasswordStrength = (pw) => {
  if (!pw) return { label: '', color: '', score: 0 };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { label: 'Weak', color: '#f44336', score };
  if (score <= 3) return { label: 'Medium', color: '#ff9800', score };
  return { label: 'Strong', color: '#4caf50', score };
};

const ForcePasswordChange = ({ onSuccess }) => {
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const strength = getPasswordStrength(newPw);

  const handleSubmit = async () => {
    setError('');
    if (newPw.length < 8) return setError('Password must be at least 8 characters.');
    if (!/[A-Z]/.test(newPw)) return setError('Password must include one uppercase letter.');
    if (!/[a-z]/.test(newPw)) return setError('Password must include one lowercase letter.');
    if (!/[0-9]/.test(newPw)) return setError('Password must include one number.');
    if (!/[^A-Za-z0-9]/.test(newPw)) return setError('Password must include one special character.');
    if (newPw !== confirmPw) return setError('Passwords do not match.');
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(API + '/users/profile', { password: newPw }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight={700} color="#003e6f">Change Your Password</Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              You must set a new password before continuing.
            </Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField fullWidth label="New Password" size="small"
              type={showNew ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNew(v => !v)}>
                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600 }}>{showNew ? 'HIDE' : 'SHOW'}</Typography>
                  </IconButton>
                </InputAdornment>
              )}}
            />
            {newPw && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress variant="determinate" value={strength.score / 5 * 100}
                    sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#eee',
                      '& .MuiLinearProgress-bar': { bgcolor: strength.color, borderRadius: 3 }}} />
                  <Typography variant="caption" sx={{ color: strength.color, fontWeight: 700, minWidth: 50 }}>{strength.label}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Use 8+ chars with uppercase, lowercase, number and special character
                </Typography>
              </Box>
            )}
            <TextField fullWidth label="Confirm New Password" size="small"
              type={showConfirm ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
              error={!!confirmPw && newPw !== confirmPw}
              helperText={!!confirmPw && newPw !== confirmPw ? 'Passwords do not match' : ''}
              InputProps={{ endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowConfirm(v => !v)}>
                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600 }}>{showConfirm ? 'HIDE' : 'SHOW'}</Typography>
                  </IconButton>
                </InputAdornment>
              )}}
            />
            <Button variant="contained" fullWidth onClick={handleSubmit} disabled={loading}
              sx={{ bgcolor: '#E8622A', '&:hover': { bgcolor: '#c8501f' }, fontWeight: 700, py: 1.2 }}>
              {loading ? 'Updating...' : 'Set New Password'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForcePasswordChange;
